import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  FormControlLabel,
  Checkbox,
  Alert,
} from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { ContactAlerts, useContactSubmit } from '../services/contactService';

const AccountDeletionRequestDialog = ({ open, onClose }) => {
  const { user } = useAuth();
  const { submit, loading, error, sent, resetStatus } = useContactSubmit();
  const [reason, setReason] = useState('');
  const [confirmed, setConfirmed] = useState(false);

  const resetForm = () => {
    setReason('');
    setConfirmed(false);
    resetStatus();
  };

  const handleSubmit = async () => {
    if (!reason.trim() || !confirmed) {
      return;
    }
    await submit({
      type: 'account_deletion',
      reason,
      name: user?.user_metadata?.first_name && user?.user_metadata?.last_name
        ? `${user.user_metadata.first_name} ${user.user_metadata.last_name}`
        : undefined,
      email: user?.email,
    });
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const isFormValid = reason.trim().length > 0 && confirmed;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Delete Account Request
      </DialogTitle>
      <DialogContent>
        <ContactAlerts error={error} sent={sent} />
        <Alert severity="warning" sx={{ mb: 3, mt: sent || error ? 2 : 0 }}>
          <Typography variant="body2">
            <strong>Warning:</strong> Account deletion is permanent and irreversible.
          </Typography>
        </Alert>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Please provide a reason for deleting your account. This helps us improve our service.
        </Typography>

        <TextField
          fullWidth
          multiline
          rows={4}
          label="Reason for Deletion"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Please explain why you want to delete your account..."
          inputProps={{ maxLength: 500 }}
          helperText={`${reason.length}/500 characters`}
          sx={{ mb: 2 }}
        />

        <FormControlLabel
          control={
            <Checkbox
              checked={confirmed}
              onChange={(e) => setConfirmed(e.target.checked)}
              color="error"
            />
          }
          label={
            <Typography variant="body2" color="error">
              I understand that account deletion is permanent and cannot be undone.
            </Typography>
          }
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="primary">
          {sent ? 'Close' : 'Cancel'}
        </Button>
        {!sent && (
          <Button 
            onClick={handleSubmit} 
            variant="contained" 
            color="error"
            disabled={!isFormValid || loading}
          >
            {loading ? 'Sending...' : 'Send Request'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default AccountDeletionRequestDialog;
